import type { ElementQuantityName } from '@shared/models/element-quantities.model';
import type {
  ExpressionValue,
  Baseline,
  IElementID,
  Project,
} from '@shared/models/project.model';
import {
  defaultVariables,
  getExpressionVariablesRecord,
  getVariablesById,
  type ExpressionVariables,
} from '@shared/helpers/expression-variables.helpers';
import { cacheFactory } from '@shared/helpers/function.helpers';
import { isExpressionValue } from '@shared/helpers/expression-factory.helpers';
import {
  getElementProperties,
  isElementExpressionProperty,
} from '@shared/helpers/element-property.helpers';
import { isElementQuantityExpressionProperty } from '@shared/helpers/element-quantity.helpers';
import { guards } from '@shared/helpers/typeguards.helpers';
import {
  getBuilding,
  getElementById,
  isElement,
} from '@shared/helpers/recursive-element.helpers';
import {
  ProjectChangeType,
  type ProjectChange,
} from '@shared/helpers/pipeline/update-engine.models';
import {
  buildElementPathIndex,
  type RelativePathIndexMap,
} from '@shared/helpers/pipeline/batch-update.helpers';
import { getQuantityValueOverrideExpression } from '@shared/helpers/element-quantity-value-override.helpers';
import { SolveBoundViolation } from '@shared/helpers/expression-solving.helpers';
import { logValidationError } from '@shared/validation/validation.helpers';
import { validateExpressionNumericOverrideForApply } from './validate-expression-numeric-override-for-apply.helpers';

/**
 * One {@link Baseline} per element id — the version whose tree contains that id
 * (via {@link buildElementPathIndex} / {@link RelativePathIndexMap}).
 */
const buildElementIdToContainingVersionMap = (
  project: Project,
): Map<IElementID, Baseline> => {
  const map = new Map<IElementID, Baseline>();
  for (const version of project.baselines) {
    const index: RelativePathIndexMap = buildElementPathIndex(version);
    for (const id of index.keys()) {
      map.set(id, version);
    }
  }
  return map;
};

/**
 * Align apply-layer expression checks with UI / pipeline: use per-element variables from
 * {@link getExpressionVariablesRecord} instead of bare {@link defaultVariables}.
 *
 * @param project - Baseline project
 * @param elementId - Target element for {@link getVariablesById}
 * @param elementToVersion - Map from {@link buildElementIdToContainingVersionMap}
 */
const getSolveVariablesForApplyValidation = (
  project: Project,
  elementId: IElementID,
  elementToVersion: Map<IElementID, Baseline>,
): ExpressionVariables => {
  const version = elementToVersion.get(elementId);
  if (!version) return defaultVariables;

  const record = cacheFactory(
    () => getExpressionVariablesRecord(project, version, [], undefined, []),
    `applyValidation:getExpressionVariablesRecord:${project.id}:${version.id}`,
    [project, version],
  );
  return getVariablesById(record, elementId);
};

const errorIfInvalidExpressionValueOverride = (
  value: number,
  expressionLine: string,
  min: number | undefined,
  max: number | undefined,
  variables: ExpressionVariables,
): Error | undefined =>
  validateExpressionNumericOverrideForApply(value, expressionLine, variables, {
    min,
    max,
  });

const errorIfInvalidCountPayload = (
  count: ExpressionValue | undefined,
  expressionLine: string,
  min: number | undefined,
  max: number | undefined,
  variables: ExpressionVariables,
): Error | undefined => {
  if (!count || !guards.is.number(count.value)) return undefined;
  return errorIfInvalidExpressionValueOverride(
    count.value,
    expressionLine,
    min,
    max,
    variables,
  );
};

/**
 * Lightweight pre-flight checks for numeric expression overrides (min/max and basic solve).
 * Full variable resolution and pipeline validation run during enrichment.
 *
 * @param project - Current project snapshot (baseline tree)
 * @param changes - Pending {@link ProjectChange} list
 * @returns First validation error, or `undefined` when checks pass
 */
export const getApplyProjectChangesExpressionOverrideValidationError = (
  project: Project,
  changes: ProjectChange[],
): Error | undefined => {
  if (changes.length === 0) return undefined;

  const elementToVersion = cacheFactory(
    () => buildElementIdToContainingVersionMap(project),
    `applyValidation:elementIdToContainingVersion:${project.id}`,
    [project],
  );

  for (const change of changes) {
    if (!('elementId' in change)) continue;
    const element = getElementById(getBuilding(project), change.elementId);
    if (!isElement(element)) continue;

    const variables = getSolveVariablesForApplyValidation(
      project,
      change.elementId,
      elementToVersion,
    );

    if (change.type === ProjectChangeType.ElementPropertyValue) {
      if (!guards.is.number(change.overrideValue)) continue;
      const property = getElementProperties(element).find(
        (p) => p.name === change.propertyName,
      );
      if (!property || !isElementExpressionProperty(property)) continue;
      const count = property.count;
      const expressionLine = isExpressionValue(count)
        ? (count.expression ?? '')
        : '';
      const err = errorIfInvalidExpressionValueOverride(
        change.overrideValue,
        expressionLine,
        property.min,
        property.max,
        variables,
      );
      if (err) return err;
      continue;
    }

    if (change.type === ProjectChangeType.ElementQuantityValue) {
      if (!guards.is.number(change.overrideValue)) continue;
      const row =
        element.quantity?.[change.quantityName as ElementQuantityName];
      if (!row || !isElementQuantityExpressionProperty(row)) continue;
      // May still be inherit text when `count` already holds a formula; solve sanitizes that.
      const expressionLine = getQuantityValueOverrideExpression(row);
      const err = errorIfInvalidExpressionValueOverride(
        change.overrideValue,
        expressionLine,
        row.min,
        row.max,
        variables,
      );
      if (err) return err;
      continue;
    }

    if (change.type === ProjectChangeType.ElementPropertyCount) {
      const property = getElementProperties(element).find(
        (p) => p.name === change.propertyName,
      );
      if (!property || !isElementExpressionProperty(property)) continue;
      const patch = change.count;
      if (!isExpressionValue(patch) || !guards.is.number(patch.value)) continue;
      const base = isExpressionValue(property.count)
        ? property.count
        : undefined;
      const expressionLine = patch.expression ?? base?.expression ?? '';
      const err = errorIfInvalidCountPayload(
        patch,
        expressionLine,
        property.min,
        property.max,
        variables,
      );
      if (err) return err;
      continue;
    }

    if (change.type === ProjectChangeType.ElementQuantityCount) {
      const row =
        element.quantity?.[change.quantityName as ElementQuantityName];
      if (!row || !isElementQuantityExpressionProperty(row)) continue;
      const patch = change.count;
      if (!isExpressionValue(patch) || !guards.is.number(patch.value)) continue;
      const base = isExpressionValue(row.count)
        ? row.count
        : isExpressionValue(row.fallbackCount)
          ? row.fallbackCount
          : undefined;
      const expressionLine = patch.expression ?? base?.expression ?? '';
      const err = errorIfInvalidCountPayload(
        patch,
        expressionLine,
        row.min,
        row.max,
        variables,
      );
      if (err) return err;
    }
  }

  return undefined;
};

/**
 * Logs an apply-layer numeric override failure.
 * A {@link SolveBoundViolation} is a typed value outside min/max. Callers show
 * that message to the user, so it is not written to the console.
 * @param error - Failure from {@link getApplyProjectChangesExpressionOverrideValidationError}
 */
export const logApplyProjectChangesValidationError = (error: Error): void => {
  // Typed min/max failures are shown to the user. console.error is reported to Sentry.
  if (error instanceof SolveBoundViolation) return;
  logValidationError(error);
};

/**
 * Text shown for an apply pre-flight failure.
 * Returns `undefined` when {@link showErrors} is omitted or false, when there is
 * no error, or when the error text is empty. A string {@link showErrors} replaces
 * the error text.
 * @param showErrors - `true` uses the error text. A string replaces it. Falsy hides it.
 * @param validationError - Failure from {@link getApplyProjectChangesExpressionOverrideValidationError}
 */
export const getApplyProjectChangesValidationErrorMessage = (
  showErrors: boolean | string | undefined,
  validationError: Error | string | undefined,
): string | undefined => {
  if (!showErrors) return undefined;
  if (!validationError) return undefined;
  if (guards.is.string(showErrors)) return showErrors;
  const fromError = guards.is.string(validationError)
    ? validationError
    : validationError.message;
  return fromError === '' ? undefined : fromError;
};
